'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import ProductCard from '@/components/ProductCard';
import type { Product } from '@/lib/products';
import { useI18n } from '@/i18n/LocaleProvider';

interface FeaturedProductsSectionProps {
  products: Product[];
}

export default function FeaturedProductsSection({ products }: FeaturedProductsSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    const children = section.querySelectorAll('.stagger-child');
    children.forEach((child, i) => {
      (child as HTMLElement).style.transitionDelay = `${i * 100}ms`;
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="stagger-child reveal-section mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold tracking-widest uppercase text-accent mb-2 block">{t.featured.eyebrow}</span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
            {t.featured.title}
          </h2>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <div className="flex items-center gap-2 glass-card rounded-full px-4 py-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-semibold text-muted-foreground">{t.featured.restocking}</span>
          </div>
          <Link
            href="/category"
            className="text-sm font-semibold text-primary hover:text-accent transition-colors flex items-center gap-1"
          >
            {t.featured.viewAll}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Product Grid — 3 cols desktop, 2 tablet, 1 mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((product) => (
          <div key={product.id} className="stagger-child">
            <ProductCard product={product} />
          </div>
        ))}
      </div>
    </section>
  );
}